/*
 * <ps-scroller> - shared behaviour for the Poundsale homepage carousels (product carousel, reviews).
 *
 * Markup contract:
 *   <ps-scroller>
 *     <button data-prev>..</button> <button data-next>..</button>
 *     <ul data-track>...items...</ul>
 *   </ps-scroller>
 *
 * The track is a native scroll-snap container (swipe / trackpad / keyboard scrolling work without JS).
 * The buttons scroll by one page of items and are disabled at either end; they are hidden
 * (via the data-static attribute) when everything already fits.
 */
if (!customElements.get('ps-scroller')) {
  class PsScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      if (!this.track) return;

      this.update = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          const max = this.track.scrollWidth - this.track.clientWidth;
          const pos = Math.abs(this.track.scrollLeft);
          this.toggleAttribute('data-static', max <= 2);
          if (this.prevBtn) this.prevBtn.disabled = pos <= 2;
          if (this.nextBtn) this.nextBtn.disabled = pos >= max - 2;
        });
      };

      const scrollByPage = (dir) => {
        const first = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const step = first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
        const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({ left: dir * perPage * step, behavior: reduce ? 'auto' : 'smooth' });
      };

      if (this.prevBtn) this.prevBtn.addEventListener('click', () => scrollByPage(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => scrollByPage(1));
      this.track.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update, { passive: true });
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(this.update);
        this.ro.observe(this.track);
      }
      this.update();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.update);
      if (this.ro) this.ro.disconnect();
    }
  }
  customElements.define('ps-scroller', PsScroller);
}
